import { useState } from 'react'
import { Form, LinkBase, required, useLogin, useNotify } from 'ra-core'
import type { SubmitHandler, FieldValues } from 'react-hook-form'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Separator } from '@internal/ui-kit/components/ui/separator'
import { TextInput } from '@internal/ui-kit/components/admin/text-input'
import { Notification } from '@internal/ui-kit/components/admin/notification'
import { extractErrorMessage } from '@internal/ui-kit/lib/httpClient'
import { startGoogleLogin } from '@/providers/authProvider'

/**
 * Sign in page: email/password (posted to the backend's stateless JWT login)
 * plus a "Continue with Google" button that hands off to the backend-brokered
 * OAuth flow (see docs/retool-migration-guide.md and the ticket spec).
 */
export const SignInPage = (props: { redirectTo?: string }) => {
  const { redirectTo } = props
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const login = useLogin()
  const notify = useNotify()

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true)
    login(values, redirectTo)
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)
        notify(extractErrorMessage(error, 'ra.auth.sign_in_error'), { type: 'error' })
      })
  }

  const handleGoogleClick = () => {
    setGoogleLoading(true)
    startGoogleLogin(redirectTo).catch((error) => {
      setGoogleLoading(false)
      notify(extractErrorMessage(error, 'Could not start Google sign in.'), { type: 'error' })
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm leading-none text-muted-foreground">Marketplace Admin</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          disabled={googleLoading}
          onClick={handleGoogleClick}
        >
          Continue with Google
        </Button>
        <div className="flex items-center gap-2">
          <Separator className="flex-1" />
          <span className="text-xs text-muted-foreground">OR</span>
          <Separator className="flex-1" />
        </div>
        <Form className="space-y-8" onSubmit={handleSubmit}>
          <TextInput label="Email" source="email" type="email" validate={required()} />
          <TextInput label="Password" source="password" type="password" validate={required()} />
          <Button type="submit" className="w-full cursor-pointer" disabled={loading}>
            Sign in
          </Button>
        </Form>
        <div className="flex justify-center text-sm text-muted-foreground">
          <LinkBase to="/request-password-reset">Forgot password?</LinkBase>
        </div>
      </div>
      <Notification />
    </div>
  )
}
